import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import {
  createRiskRegisterItemApi,
  decideEmsApprovalApi,
  deleteRiskRegisterItemApi,
  downloadRiskRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchRiskRegisterApi,
  requestEmsApprovalApi,
  updateRiskRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "risk_opportunities";

const EMPTY_FORM = {
  iso_clause: "",
  type: "risk",
  title: "",
  description: "",
  related_context: "",
  impact: "",
  likelihood: "",
  rating: "",
  control: "",
  owner: "",
  status: "open",
  review_date: "",
  evidence: "",
  notes: "",
};

export default function RiskOpportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchRiskRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load risk register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("6");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "6" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Title is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "closed") {
      if (!String(form.description || "").trim()) errs.push("Description is required before setting status to closed.");
      if (!String(form.impact || "").trim()) errs.push("Impact is required before setting status to closed.");
      if (!String(form.likelihood || "").trim()) errs.push("Likelihood is required before setting status to closed.");
      if (!String(form.rating || "").trim()) errs.push("Rating is required before setting status to closed.");
      if (!String(form.control || "").trim()) errs.push("Control is required before setting status to closed.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting status to closed.");
      if (!String(form.review_date || "").trim()) errs.push("Review date is required before setting status to closed.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to closed.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("risk_opportunities");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["closed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      type: item.type || "risk",
      title: item.title || "",
      description: item.description || "",
      impact: item.impact || "",
      likelihood: item.likelihood || "",
      rating: item.rating || "",
      control: item.control || "",
      owner: item.owner || "",
      status: item.status || "open",
      review_date: item.review_date || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateRiskRegisterItemApi(editingId, form);
        toast.success("Risk/opportunity updated");
      } else {
        await createRiskRegisterItemApi(form);
        toast.success("Risk/opportunity created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save risk/opportunity");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this risk/opportunity entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteRiskRegisterItemApi(id);
      toast.success("Entry deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete entry");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadRiskRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "risk_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Risk register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export risk register");
    } finally {
      setExporting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.owner || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterType !== "all" && String(it.type || "") !== filterType) return false;
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterType, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "open_risks") {
      setFilterType("risk");
      setFilterStatus("open");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "open_opportunities") {
      setFilterType("opportunity");
      setFilterStatus("open");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterType("all");
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    setFilterType("all");
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page guided-register-page">
      <div className="dashboard-header">
        <div>
          <p className="guided-register-eyebrow">ISO 14001 planning</p>
          <h1>Risk & Opportunities Register</h1>
          <p className="subtitle">Record environmental risks and improvement opportunities, assess their significance, and track the controls and actions used to manage them.</p>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to complete an entry</p>
          <h3>Describe the issue, assess it, then define the response</h3>
          <p>Use a risk for an uncertain event with a potential negative environmental consequence, or an opportunity for a realistic improvement or benefit.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Identify</strong><span>State the risk or opportunity clearly and describe the environmental issue or benefit.</span></div>
          <div><strong>Assess</strong><span>Use the agreed impact and likelihood scale to record a rating consistently.</span></div>
          <div><strong>Respond</strong><span>Assign an owner, control or mitigation, evidence and review date.</span></div>
        </div>
      </section>
      <div className="card settings-card guided-register-form">
        <div className="settings-header">
          <div><p className="guided-register-eyebrow">Your risk or opportunity</p><h3>{editingId ? "Edit Entry" : "Add Entry"}</h3></div>
          <span className="kpi-sub">Use the agreed risk scoring approach and keep the response proportionate to the issue.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Type</span>
              <select value={form.type} onChange={(e) => setField("type", e.target.value)} disabled={saving}>
                <option value="risk">risk</option>
                <option value="opportunity">opportunity</option>
              </select>
              <small>Select risk for a potential adverse outcome, or opportunity for a potential environmental improvement.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="open">open</option>
                <option value="monitoring">monitoring</option>
                <option value="closed">closed</option>
              </select>
              <small>Use open while action is needed, monitoring while controls are checked, and closed only when evidence supports closure.</small>
            </label>
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Risk of fuel spill during deliveries" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Give the entry a short name that identifies the event or improvement opportunity.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Logistics Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role accountable for managing the response and review.</small>
            </label>
            <label className="settings-field">
              <span>Impact</span>
              <input placeholder="e.g. Soil or water contamination" value={form.impact} onChange={(e) => setField("impact", e.target.value)} disabled={saving} />
              <small>Describe the potential consequence or environmental benefit.</small>
            </label>
            <label className="settings-field">
              <span>Likelihood</span>
              <input placeholder="e.g. 2" value={form.likelihood} onChange={(e) => setField("likelihood", e.target.value)} disabled={saving} />
              <small>Use the agreed likelihood scale from your governance policy.</small>
            </label>
            <label className="settings-field">
              <span>Rating</span>
              <input placeholder="e.g. 8 - medium" value={form.rating} onChange={(e) => setField("rating", e.target.value)} disabled={saving} />
              <small>Record the agreed overall rating after considering impact and likelihood.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving} />
              <small>Set the next review date and bring it forward after an incident, change or control failure.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Description</span>
              <input placeholder="Describe the cause, situation, affected activity and possible consequence or benefit." value={form.description} onChange={(e) => setField("description", e.target.value)} disabled={saving} />
              <small>Include enough detail for another person to understand why the entry exists.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Related Context issue / condition</span>
              <input placeholder="e.g. Severe-weather travel disruption; supplier environmental-data availability" value={form.related_context} onChange={(e) => setField("related_context", e.target.value)} disabled={saving} />
              <small>Optional: link this risk or opportunity to a Context issue, environmental condition, interested-party expectation, lifecycle consideration or strategic decision.</small>
            </label>
            <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
              <span>Control / mitigation</span>
              <input placeholder="e.g. Driver spill-kit checks, delivery procedure and incident-response training." value={form.control} onChange={(e) => setField("control", e.target.value)} disabled={saving} />
              <small>Record the existing or planned action that prevents, reduces, manages or realises the issue.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link risk assessments, inspections, incident records, action plans, procedures or other evidence supporting this entry."
            />
            <label className="settings-field guided-register-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record assumptions, action progress, incidents or review decisions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update entry" : "Add entry"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Type</span>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">all</option>
            <option value="risk">risk</option>
            <option value="opportunity">opportunity</option>
          </select>
              <small>Select risk for a potential adverse outcome, or opportunity for a potential environmental improvement.</small>
        </label>

        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="open">open</option>
            <option value="monitoring">monitoring</option>
            <option value="closed">closed</option>
          </select>
              <small>Use open while action is needed, monitoring while controls are checked, and closed only when evidence supports closure.</small>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue review only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("open_risks")}>Open risks</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("open_opportunities")}>Open opportunities</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Review due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Type</th>
              <th>Title</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Rating</th>
              <th>Review date</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={13} className="table-empty">No risk/opportunity entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.type || "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.owner || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.rating || "-"}</td>
                  <td>{it.review_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



